import type { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import CustomLoader from 'components/molecules/CustomLoader.component';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { getCustomerIdFromPath } from 'utils/helpers';

interface CustomerDataGuardProps {
  children: ReactNode;
}

/**
 * Ensures customer data is loaded before rendering children in expert app
 * Prevents crashes from accessing undefined customer data
 */
const CustomerDataGuard: FC<CustomerDataGuardProps> = ({ children }) => {
  const customerId = getCustomerIdFromPath();
  const { data: customer, isLoading, isError } = useGetCurrentCustomerData();

  // No customer in URL → no guard needed
  if (!customerId) {
    return <>{children}</>;
  }

  // Loading customer data → show loader
  if (isLoading) {
    return <CustomLoader className="h-screen" color="primary" />;
  }

  // API error or customer not found → redirect back to customer list
  if (isError || !customer) {
    return <Navigate to="/" replace />;
  }

  // Customer data loaded successfully
  return <>{children}</>;
};

export default CustomerDataGuard;
